import { Loader2, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Modal } from '@/components/modal';

interface RevealNewTokenModalProps {
  token: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * One-time reveal of a freshly created personal access token. The plaintext
 * token is only ever available on the create response (CreatedUserPat.token);
 * once this modal closes it can never be retrieved again.
 */
export const RevealNewTokenModal = ({
  token,
  open,
  onOpenChange,
}: RevealNewTokenModalProps) => (
  <Modal
    isDialogOpen={open}
    setIsDialogOpen={onOpenChange}
    title='Your new API token'
    description="Copy this token now. For security, you won't be able to see it again."
    contentClassName='sm:max-w-md'
    footer={<Button onClick={() => onOpenChange(false)}>Done</Button>}
  >
    <div className='space-y-3'>
      <div className='flex items-center gap-2'>
        <code className='flex-1 rounded-md bg-muted px-3 py-2 text-xs font-mono break-all select-all'>
          {token}
        </code>
        <div className='shrink-0'>
          <CopyButton text={token ?? ''} ariaLabel='Copy API token' />
        </div>
      </div>
      <p className='flex items-start gap-2 text-xs text-muted-foreground'>
        <TriangleAlert className='size-4 shrink-0 text-amber-500' />
        <span>
          Store this token somewhere safe. Anyone with this token can make API
          requests as you.
        </span>
      </p>
    </div>
  </Modal>
);

interface RevokeTokenModalProps {
  tokenName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}

export const RevokeTokenModal = ({
  tokenName,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: RevokeTokenModalProps) => (
  <Modal
    isDialogOpen={open}
    setIsDialogOpen={onOpenChange}
    title='Revoke API token'
    description={
      <>
        This action cannot be undone. Any requests using{' '}
        <span className='font-medium text-foreground'>
          {tokenName ?? 'this token'}
        </span>{' '}
        will stop working immediately.
      </>
    }
    contentClassName='sm:max-w-sm'
    footer={
      <div className='flex gap-2'>
        <Button
          variant='outline'
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button variant='destructive' onClick={onConfirm} disabled={isPending}>
          {isPending && <Loader2 className='size-4 animate-spin mr-1' />}
          Revoke token
        </Button>
      </div>
    }
  />
);
